/**
 * @file color-contrast.ts
 * @description Контраст для поля цвета и окна «Свой цвет» (phase19.16, партия
 * SB120 — предупреждение, не запрет; uiux-sb120-fields §3). Расчёт WCAG — из
 * shared (`contrastRatio`, `contrastLevel`, `resolveSurfaceFill`,
 * `surfaceToneOf`), здесь только сборка «текст на фоне» для двух режимов:
 *  - text — цвет текста на фоне под элементом;
 *  - fill — текст, который платформа подберёт по тону заливки, на заливке.
 * Битый цвет черновика — `null` (считать не с чем), а не исключение.
 */

import {
  contrastLevel,
  contrastRatio,
  formatContrast,
  requireHexColor,
  resolveSurfaceFill,
  surfaceToneOf,
  type SiteColors,
  type SiteContrastLevel,
  type SiteSurfaceFill,
} from "@chaiberi/shared";

/** Норма обычного текста (WCAG AA): «Взять читаемый» ищет роль не ниже. */
export const READABLE_MIN = 4.5;

export interface ContrastView {
  ratio: number;
  level: SiteContrastLevel;
  /** «7,2:1» — для людей. */
  label: string;
}

/** Число → уровень ✓/!/✕ и подпись. */
export function contrastView(ratio: number): ContrastView {
  return { ratio, level: contrastLevel(ratio, "text"), label: `${formatContrast(ratio)}:1` };
}

/** Фон непрозрачным: полупрозрачный лежит на фоне страницы, как на витрине. */
export function opaqueBg(color: string, colors: SiteColors): string | null {
  try {
    return resolveSurfaceFill({ kind: "custom", color }, { colors }).background;
  } catch {
    return null;
  }
}

/** Контраст текста `fg` на фоне `bg`; `null` — цвет неверен. */
export function textContrast(fg: string, bg: string, colors: SiteColors): number | null {
  const background = opaqueBg(bg, colors);
  if (!background) return null;
  try {
    return contrastRatio(requireHexColor(fg), requireHexColor(background));
  } catch {
    return null;
  }
}

/** Цвет текста «Авто» на фоне: «Текст» или «На тёмном» — что читается лучше. */
export function autoTextColor(bg: string, colors: SiteColors): string | null {
  try {
    return surfaceToneOf(bg, colors) === "dark" ? colors.onDark : colors.text;
  } catch {
    return null;
  }
}

/** Заливка: фон и цвет текста, который подберёт платформа; `null` — цвет неверен. */
export function fillTextColors(fill: SiteSurfaceFill, colors: SiteColors): { background: string; text: string } | null {
  try {
    const { background, tone } = resolveSurfaceFill(fill, { colors });
    return { background, text: tone === "dark" ? colors.onDark : colors.text };
  } catch {
    return null;
  }
}

/** Контраст текста на заливке. */
export function fillContrast(fill: SiteSurfaceFill, colors: SiteColors): number | null {
  const pair = fillTextColors(fill, colors);
  return pair ? textContrast(pair.text, pair.background, colors) : null;
}
